import type { ReactNode } from 'react';

import { createTheme, GlobalStyles, Link, type Theme, ThemeProvider } from '@mui/material';
import { captureException } from '@sentry/react';
import { clsx } from 'clsx';
import { useHistory } from 'react-router-dom';

import { StatusPageInfo } from 'app/integrations/StatusPageInfo';
import gilroyMediumUrl from 'assets/fonts/Gilroy-Medium.woff2';
import illustrationUrl from 'assets/images/authHero/dashboard-illustration.webp';
import americaFirstLogo from 'assets/images/authHero/logo-america-first.png';
import crossCountryLogo from 'assets/images/authHero/logo-crosscountry.png';
import fannieMaeLogo from 'assets/images/authHero/logo-fannie-mae.png';
import fifthThirdLogo from 'assets/images/authHero/logo-fifth-third.png';
import navyFederalLogo from 'assets/images/authHero/logo-navy-federal.png';
import okdhsLogo from 'assets/images/authHero/logo-okdhs.png';
import { useAuth } from 'shared/lib/auth/useAuth';
import { MOBILE_BREAKPOINT } from 'shared/lib/screen/useScreenSize';
import { useStatusPageIncident } from 'shared/lib/statusPage/useStatusPageIncident';
import LogoIcon from 'shared/ui/icons/logo.svg?react';

// Source PNGs are ~4x the display height, so they stay sharp on retina.
const CUSTOMER_LOGOS = [
  { src: fannieMaeLogo, alt: 'Fannie Mae', height: 24 },
  { src: americaFirstLogo, alt: 'America First Credit Union', height: 32 },
  { src: okdhsLogo, alt: 'Oklahoma Human Services', height: 30 },
  { src: fifthThirdLogo, alt: 'Fifth Third', height: 22 },
  { src: navyFederalLogo, alt: 'Navy Federal Credit Union', height: 32 },
  { src: crossCountryLogo, alt: 'CrossCountry Mortgage', height: 24 },
];

const LEGAL_LINKS = [
  { label: 'Privacy Policy', href: 'https://truv.com/privacy' },
  { label: 'Terms', href: 'https://truv.com/terms' },
];

const HERO_GRADIENT = 'linear-gradient(135deg, #5B82F0 0%, #3366E6 45%, #2A5BDA 100%)';
const CARD_SHADOW = '0 24px 60px rgba(20, 45, 140, 0.25)';
const MESSAGE_BACKGROUND = '#2D5DDA';

const GILROY_FONT_FACE = {
  '@font-face': {
    fontFamily: 'Gilroy',
    src: `url(${gilroyMediumUrl}) format("woff2")`,
    fontWeight: 500,
    fontStyle: 'normal',
    fontDisplay: 'swap',
  },
};

// The dashboard theme sets 15px input text on phones; repeat the design's values there so it doesn't win.
const MOBILE = `@media (max-width: ${MOBILE_BREAKPOINT - 1}px)`;
// Inputs from the design: 16px text on a 22px line, 18px side padding, 12px corners.
const INPUT_TEXT = { fontSize: 16, borderRadius: 12 };
const INPUT_LINE = { height: 22, lineHeight: '22px' };

// MUI copies the font family into each typography variant when the theme is created, so the variants need it too.
const authHeroTheme = (outerTheme: Theme) => {
  const fontFamily = `Gilroy, ${outerTheme.typography.fontFamily}`;
  const variants = Object.entries(outerTheme.typography)
    .filter(([, variant]) => typeof variant === 'object' && variant !== null && 'fontFamily' in variant)
    .map(([name]) => [name, { fontFamily }]);

  return createTheme(outerTheme, {
    typography: { fontFamily, ...Object.fromEntries(variants) },
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: { ...INPUT_TEXT, [MOBILE]: INPUT_TEXT },
          input: { ...INPUT_LINE, padding: '12px 18px', [MOBILE]: INPUT_LINE },
        },
      },
    },
  });
};

type AuthHeroLayoutProps = {
  children: ReactNode;
  /** `form` puts the content in a white card beside the illustration; `message` centres it on the gradient. */
  variant?: 'form' | 'message';
  /** Bottom line inside the gradient. Defaults to the Privacy Policy / Terms links. */
  footer?: ReactNode;
};

const LegalLinks = () => (
  <div className="flex gap-6">
    {LEGAL_LINKS.map(({ label, href }) => (
      <Link
        key={href}
        href={href}
        target="_blank"
        underline="always"
        className="text-[13px] text-white/70 transition-colors hover:text-white"
        sx={{ textDecorationColor: 'currentColor' }}
      >
        {label}
      </Link>
    ))}
  </div>
);

/** The redesigned login layout (dashboard_login's AculLayout), for the auth screens that run inside the dashboard. */
export const AuthHeroLayout = ({ children, variant = 'form', footer = <LegalLinks /> }: AuthHeroLayoutProps) => {
  const { logout } = useAuth();
  const history = useHistory();
  const { data: statusPageIncident } = useStatusPageIncident();

  const handleLogout = async () => {
    try {
      await logout();
      history.push('/');
    } catch (error) {
      captureException(error);
    }
  };

  return (
    <ThemeProvider theme={authHeroTheme}>
      <GlobalStyles styles={GILROY_FONT_FACE} />
      {statusPageIncident && <StatusPageInfo incident={statusPageIncident} />}
      <div className="flex min-h-full flex-col bg-white px-4 md:px-8">
        <header className="flex items-center justify-between py-6">
          <LogoIcon width={72} />
          <Link
            component="button"
            underline="hover"
            color="text.primary"
            className="text-[15px] font-medium"
            onClick={() => void handleLogout()}
          >
            Log out
          </Link>
        </header>

        <main
          className={clsx(
            'relative mb-4 grid flex-1 grid-cols-[minmax(0,1fr)] items-center justify-items-center gap-8 overflow-hidden rounded-3xl px-4 pt-6 pb-16 sm:mb-0 sm:px-8 md:min-h-[560px] md:px-14 md:pt-14 md:pb-[72px]',
            variant === 'form' ? 'md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:rounded-[32px]' : 'md:rounded-[40px]',
          )}
          style={{ background: variant === 'form' ? HERO_GRADIENT : MESSAGE_BACKGROUND }}
        >
          {variant === 'form' ? (
            <>
              <div
                className="w-full min-w-0 max-w-[600px] rounded-[20px] bg-white p-6 sm:p-9"
                style={{ boxShadow: CARD_SHADOW }}
              >
                {children}
              </div>
              <div className="hidden w-full items-center justify-center md:flex" aria-hidden>
                <img src={illustrationUrl} alt="" className="h-auto w-[90%] max-w-[760px]" />
              </div>
            </>
          ) : (
            <>
              {/* The design's radial light: a blurred ellipse centred at the top, sized in proportion to the frame. */}
              <div
                className="pointer-events-none absolute top-0 left-[19%] h-[92%] w-[62%] rounded-full bg-[rgba(74,116,255,0.6)] blur-[80px] md:blur-[200px]"
                aria-hidden
              />
              <div className="relative w-full max-w-[900px] text-center text-white md:pb-[70px]">{children}</div>
            </>
          )}

          {footer && <div className="absolute inset-x-4 bottom-6 flex justify-center text-center">{footer}</div>}
        </main>

        <div className="mx-auto hidden w-full max-w-[1240px] grid-cols-[repeat(3,auto)] items-center justify-between justify-items-center gap-8 py-8 sm:grid sm:px-4 md:grid-cols-[repeat(6,auto)] md:px-8">
          {CUSTOMER_LOGOS.map((logo) => (
            <img key={logo.alt} src={logo.src} alt={logo.alt} style={{ height: logo.height }} />
          ))}
        </div>
      </div>
    </ThemeProvider>
  );
};
